import {
  ChangeDetectionStrategy,
  Component,
  type ElementRef,
  ViewEncapsulation,
  afterRenderEffect,
  computed,
  inject,
  input,
  model,
  viewChildren,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ZagIcon } from '../icon/icon';

/**
 * Single step rendered by {@link HelpStepsComponent} and {@link HelpDialogComponent}.
 *
 * `titleKey` and `bodyKey` are i18n keys under the `helpDialog.*` namespace.
 * The body may contain safe HTML markup (links, lists, `<strong>`, `<code>`)
 * — it is rendered via `[innerHTML]` and sanitised by Angular's built-in
 * DOM sanitiser.
 *
 * `screenshot` is an optional image URL displayed above the body.
 */
export interface HelpDialogStep {
  titleKey: string;
  bodyKey: string;
  screenshot?: string;
  screenshotAlt?: string;
}

/**
 * Reusable help-step renderer used by both the help dialog (stepper mode)
 * and the AI chat (list mode) so the canonical help content lives in exactly
 * one component — no copy-paste, no AI paraphrasing.
 *
 * Responsibilities:
 * - Render each step's title + sanitised HTML body
 * - Substitute the active client origin/host into URL placeholders via
 *   ngx-translate interpolation params (`{{origin}}`, `{{host}}`)
 * - Append a copy-to-clipboard button next to every URL `<code>` example
 * - React to language changes so translations refresh live
 *
 * @example List mode (chat inline)
 * ```html
 * <zag-help-steps
 *   [steps]="steps"
 *   [titleKey]="'helpDialog.socialLogin.microsoft.title'"
 *   icon="help_outline"
 * />
 * ```
 *
 * @example Stepper mode (dialog)
 * ```html
 * <zag-help-steps mode="stepper" [steps]="steps" [(currentIndex)]="index" />
 * ```
 */
@Component({
  selector: 'zag-help-steps',
  imports: [TranslatePipe, ZagIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Encapsulation disabled so the sanitised HTML rendered via `[innerHTML]`
  // can be styled. All selectors below are scoped through the
  // `.zag-help-steps*` block to avoid leaking into the app.
  encapsulation: ViewEncapsulation.None,
  template: `
    @if (mode() === 'list' && titleKey(); as titleKey) {
      <h3 class="zag-help-steps__title">
        @if (icon()) {
          <span [zagIcon]="icon()!" class="zag-help-steps__title-icon"></span>
        }
        {{ titleKey | translate }}
      </h3>
    }

    @if (mode() === 'list') {
      @for (step of steps(); track $index; let i = $index) {
        <article class="zag-help-steps__step">
          <h4 class="zag-help-steps__step-heading">
            <span class="zag-help-steps__step-number">{{ i + 1 }}</span>
            <span>{{ step.titleKey | translate }}</span>
          </h4>
          @if (step.screenshot; as src) {
            <figure class="zag-help-steps__figure">
              <img
                [src]="src"
                [attr.alt]="step.screenshotAlt ?? ''"
                [attr.aria-hidden]="!step.screenshotAlt ? 'true' : null"
                class="zag-help-steps__image"
              />
            </figure>
          }
          <div #stepBody class="zag-help-steps__body" [innerHTML]="bodyHtmls()[i]"></div>
        </article>
      }
    } @else {
      <h4 class="zag-help-steps__step-heading">{{ currentStep().titleKey | translate }}</h4>
      @if (currentStep().screenshot; as src) {
        <figure class="zag-help-steps__figure">
          <img
            [src]="src"
            [attr.alt]="currentStep().screenshotAlt ?? ''"
            [attr.aria-hidden]="!currentStep().screenshotAlt ? 'true' : null"
            class="zag-help-steps__image"
          />
        </figure>
      }
      <div #stepBody class="zag-help-steps__body" [innerHTML]="bodyHtmls()[currentIndex()]"></div>
    }
  `,
  styles: [
    `
      zag-help-steps {
        display: block;
      }

      .zag-help-steps__title {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        margin: 0 0 var(--spacing-md);
        font-size: var(--font-size-md);
        font-weight: var(--font-weight-semibold);
        color: var(--text-color-primary);
      }

      .zag-help-steps__title-icon {
        color: var(--color-primary, var(--primary-color));
      }

      .zag-help-steps__step + .zag-help-steps__step {
        margin-top: var(--spacing-lg);
        padding-top: var(--spacing-md);
        border-top: 1px solid var(--border-color-default, var(--border-color));
      }

      .zag-help-steps__step-heading {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        margin: 0 0 var(--spacing-sm);
        font-size: var(--font-size-md);
        font-weight: var(--font-weight-semibold);
        color: var(--text-color-primary);
      }

      .zag-help-steps__step-number {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: var(--color-primary, var(--primary-color));
        color: var(--color-primary-contrast, #fff);
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-semibold);
      }

      .zag-help-steps__figure {
        margin: 0 0 var(--spacing-md);
        padding: 0;
        border-radius: var(--border-radius-md);
        overflow: hidden;
        border: 1px solid var(--border-color-default, var(--border-color));
        background: var(--surface-color-subtle, var(--background-color-subtle));
      }

      .zag-help-steps__image {
        display: block;
        width: 100%;
        height: auto;
      }

      .zag-help-steps__body {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        line-height: 1.6;
      }

      .zag-help-steps__body p {
        margin: 0 0 var(--spacing-sm);
      }

      .zag-help-steps__body ol,
      .zag-help-steps__body ul {
        margin: 0 0 var(--spacing-sm);
        padding-left: var(--spacing-lg);
      }

      .zag-help-steps__body li {
        margin-bottom: var(--spacing-xs, 4px);
      }

      .zag-help-steps__body a {
        color: var(--color-primary, var(--primary-color));
        text-decoration: underline;
      }

      .zag-help-steps__body code {
        padding: 2px 6px;
        background: var(--surface-color-subtle, var(--background-color-subtle));
        border: 1px solid var(--border-color-default, var(--border-color));
        border-radius: var(--border-radius-sm);
        font-family: var(--font-family-mono, monospace);
        font-size: 0.9em;
      }

      /* URL examples get full-width display + soft wrapping so they never bleed
         past the dialog/chat container. */
      .zag-help-steps__body code.zag-help-dialog__url {
        display: block;
        margin: var(--spacing-xs, 4px) 0 0;
        padding: var(--spacing-xs, 4px) var(--spacing-sm);
        border-radius: var(--border-radius-sm) var(--border-radius-sm) 0 0;
        word-break: break-all;
        overflow-wrap: anywhere;
      }

      /* Copy button injected next to each URL code block. Sits flush under
         the URL block so the two read as a single combined control. */
      .zag-help-dialog__copy-btn {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        margin: 0 0 var(--spacing-sm);
        padding: 4px 10px;
        background: var(--surface-color-subtle, var(--background-color-subtle));
        border: 1px solid var(--border-color-default, var(--border-color));
        border-top: none;
        border-radius: 0 0 var(--border-radius-sm) var(--border-radius-sm);
        color: var(--text-color-secondary);
        cursor: pointer;
        font-size: 0.85em;
        line-height: 1;
        transition:
          background-color 0.15s,
          color 0.15s;
      }

      .zag-help-dialog__copy-btn:hover,
      .zag-help-dialog__copy-btn:focus-visible {
        background: var(--surface-color-hover, var(--background-color-hover));
        color: var(--color-primary, var(--primary-color));
        outline: none;
      }

      .zag-help-dialog__copy-btn--copied {
        color: var(--color-success);
      }

      .zag-help-dialog__copy-btn .material-icons {
        font-size: 16px;
      }

      @media (prefers-reduced-motion: reduce) {
        .zag-help-dialog__copy-btn {
          transition: none;
        }
      }
    `,
  ],
})
export class HelpStepsComponent {
  private readonly translate = inject(TranslateService);
  private readonly document = inject(DOCUMENT);

  readonly steps = input.required<HelpDialogStep[]>();
  readonly mode = input<'stepper' | 'list'>('list');
  readonly titleKey = input<string | null>(null);
  readonly icon = input<string | null>(null);
  readonly currentIndex = model<number>(0);

  /** Current client URL pieces injected into URL example bodies via ngx-translate
   * params. Resolved once at construction time. */
  private readonly origin = this.document.defaultView?.location.origin ?? '';
  private readonly host = this.document.defaultView?.location.host ?? '';

  private readonly bodyContainers = viewChildren<ElementRef<HTMLElement>>('stepBody');

  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  protected readonly currentStep = computed(() => this.steps()[this.currentIndex()]);

  /** Sanitisable HTML for every step body, indexed by step position. */
  protected readonly bodyHtmls = computed(() => {
    this.currentLang();
    return this.steps().map(step => {
      const translated = this.translate.instant(step.bodyKey, {
        origin: this.origin,
        host: this.host,
      });
      return translated === step.bodyKey ? '' : translated;
    });
  });

  constructor() {
    // Re-run after every render of any step body so dynamically rendered URL
    // `<code>` blocks get a copy button appended. Re-fires when `currentIndex`
    // changes (stepper mode) or when the body HTML refreshes (language switch).
    afterRenderEffect(() => {
      this.currentIndex();
      this.currentLang();
      this.bodyHtmls();
      for (const ref of this.bodyContainers()) {
        this.attachCopyButtons(ref.nativeElement);
      }
    });
  }

  private attachCopyButtons(container: HTMLElement): void {
    const codes = container.querySelectorAll<HTMLElement>('code.zag-help-dialog__url');
    const copyLabel = this.translate.instant('helpDialog.copy');
    codes.forEach(code => {
      if (code.nextElementSibling?.classList.contains('zag-help-dialog__copy-btn')) return;
      const btn = this.document.createElement('button');
      btn.type = 'button';
      btn.className = 'zag-help-dialog__copy-btn';
      btn.setAttribute('aria-label', copyLabel);
      btn.title = copyLabel;
      btn.innerHTML = `<span class="material-icons" aria-hidden="true">content_copy</span><span>${copyLabel}</span>`;
      btn.addEventListener('click', () => this.copyToClipboard(code, btn));
      code.after(btn);
    });
  }

  private async copyToClipboard(source: HTMLElement, btn: HTMLButtonElement): Promise<void> {
    const text = source.textContent ?? '';
    if (!navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      return;
    }
    const copiedLabel = this.translate.instant('helpDialog.copied');
    const copyLabel = this.translate.instant('helpDialog.copy');
    btn.classList.add('zag-help-dialog__copy-btn--copied');
    btn.title = copiedLabel;
    btn.setAttribute('aria-label', copiedLabel);
    btn.innerHTML = `<span class="material-icons" aria-hidden="true">check</span><span>${copiedLabel}</span>`;
    setTimeout(() => {
      btn.classList.remove('zag-help-dialog__copy-btn--copied');
      btn.title = copyLabel;
      btn.setAttribute('aria-label', copyLabel);
      btn.innerHTML = `<span class="material-icons" aria-hidden="true">content_copy</span><span>${copyLabel}</span>`;
    }, 1500);
  }
}
